import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { useForAuthorisation } from "./context";
import { database, auth } from "./firebase";
import "./Logout.css"; // new styling

const Logout = () => {
  //logout is is used by function logout below, & it comes from context.js & is linked to context hook
  const { logout, MainUserUID: contextUID } = useForAuthorisation();

  // Use auth.currentUser as fallback if context doesn't have MainUserUID
  const MainUserUID = contextUID || auth.currentUser?.uid;

  const historyPage = useHistory();
  const [errorMessage, setErrorMessage] = useState();
  const [getUsers, setGetUsers] = useState([]);
  const [newData, setNewData] = useState([]);
  const [uid, setUID] = useState("");

  // Theme state - initialize from localStorage or default to light
  const [theme, setTheme] = useState(() =>
    typeof window !== "undefined"
      ? localStorage.getItem("app-theme") || "light"
      : "light",
  );

  // Browser dark mode detection (for inline styles)
  const [isDarkMode, setIsDarkMode] = useState(
    window.matchMedia &&
      window.matchMedia("(prefers-color-scheme: dark)").matches,
  );

  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const handler = (e) => setIsDarkMode(e.matches);
    if (media.addEventListener) media.addEventListener("change", handler);
    else media.addListener(handler);
    return () => {
      if (media.removeEventListener)
        media.removeEventListener("change", handler);
      else media.removeListener(handler);
    };
  }, []);

  // Apply theme on mount and when theme changes
  useEffect(() => {
    try {
      document.documentElement.setAttribute("data-theme", theme);
      localStorage.setItem("app-theme", theme);
    } catch (e) {
      // ignore in restricted environments
    }
  }, [theme]);

  // Read theme preference from Firebase once on load (Owner/{MainUserUID}/themePreference)
  useEffect(() => {
    if (!MainUserUID) return;
    const prefRef = database.ref(`/Owner/${MainUserUID}/themePreference`);
    prefRef
      .once("value")
      .then((snapshot) => {
        const val = snapshot.val();
        if (val) {
          setTheme(val);
          try {
            localStorage.setItem("app-theme", val);
            document.documentElement.setAttribute("data-theme", val);
          } catch (e) {}
        }
      })
      .catch((err) => {
        // ignore read errors
        console.log("theme read error", err && err.message);
      });
  }, [MainUserUID]);

  useEffect(() => {
    if (!MainUserUID) return;

    database.ref("Owner/" + MainUserUID).on("value", (snapshot) => {
      const accounts = snapshot.val();

      let allAccounts = [];
      for (let id in accounts) {
        allAccounts.push({ id, ...accounts[id] });
      }

      setGetUsers(allAccounts.filter((el) => el.MAINuserId));
    });

    return () => database.ref("Owner/" + MainUserUID).off();
  }, [MainUserUID]);

  useEffect(() => {
    if (!MainUserUID) return;

    const urlDb = "/UserSelections/" + MainUserUID;
    const newUrlDb = database.ref(urlDb);
    newUrlDb.on("value", (snapshot) => {
      const accounts = snapshot.val();

      let newAccounts = [];
      if (accounts) {
        newAccounts.push({ ...accounts });
      }

      setNewData(newAccounts);
    });
    return () => newUrlDb.off();
  }, [MainUserUID]);

  useEffect(() => {
    if (getUsers.length > 0) {
      getUsers.forEach((el) => setUID(el.MAINuserId));
    }
  }, [getUsers]);

  const [companySelectedIdKey, setcompanySelectedIdKey] = useState("");

  useEffect(() => {
    if (newData.length > 0) {
      setcompanySelectedIdKey(
        newData
          .filter((t) => t.companySelected)
          .map((el) => el.companySelected)
          .toString(),
      );
    }
  }, [newData]);

  const [CompanyNAMEDetails, setCompanyNAMEDetails] = useState("");
  const [CompanyYEAR, setCompanyYEAR] = useState("");
  const [isLoadingCompanyData, setIsLoadingCompanyData] = useState(false);

  // Track the last loaded company to prevent re-loading same data
  const lastLoadedCompanyRef = React.useRef(null);

  //FOR COMPANY NAME
  // console.log(
  //   "CompanyNAMEDetails",
  //   CompanyNAMEDetails,
  //   "companySelectedIdKey",
  //   companySelectedIdKey,
  //   '"newData"',
  //   newData
  // );
  useEffect(() => {
    // console.log("🔍 Logout useEffect running:", {
    //   MainUserUID,
    //   uid,
    //   companySelectedIdKey,
    //   hasUsers: getUsers.length > 0,
    //   hasNewData: newData.length > 0,
    //   lastLoaded: lastLoadedCompanyRef.current,
    //   isLoadingCompanyData
    // });

    // If we don't have MainUserUID, stop loading
    if (!MainUserUID) {
      // console.log("❌ No MainUserUID - stopping load");
      setIsLoadingCompanyData(false);
      return;
    }

    // Use MainUserUID as fallback if uid is not set
    const effectiveUid = uid || MainUserUID;

    // If we don't have companySelectedIdKey, wait briefly then stop loading
    if (!companySelectedIdKey) {
      // console.log("⏳ No companySelectedIdKey - setting timeout to stop loading");
      const noCompanyTimeout = setTimeout(() => {
        // console.log("⏱️ No company selected after 500ms - stopping load");
        setIsLoadingCompanyData(false);
        setCompanyNAMEDetails("");
        setCompanyYEAR("");
      }, 500);

      return () => clearTimeout(noCompanyTimeout);
    }

    // If we have both effectiveUid and companySelectedIdKey, proceed with loading
    if (effectiveUid && companySelectedIdKey) {
      // Check if we've already loaded this company - prevent re-loading
      const companyKey = `${effectiveUid}_${companySelectedIdKey}`;
      if (lastLoadedCompanyRef.current === companyKey) {
        // Already loaded this company, don't reload
        //  console.log("✅ Company already loaded, skipping");
        setIsLoadingCompanyData(false);
        return;
      }

      // console.log("📥 Starting company data load with effectiveUid:", effectiveUid);
      setIsLoadingCompanyData(true);
      lastLoadedCompanyRef.current = companyKey;

      let nameLoaded = false;
      let yearLoaded = false;
      let timeoutId = null;

      const checkLoadingComplete = () => {
        if (nameLoaded && yearLoaded) {
          // console.log("✅ Both name and year loaded");
          setIsLoadingCompanyData(false);
          if (timeoutId) clearTimeout(timeoutId);
        }
      };

      // Set 1.5-second timeout to stop loading (aggressive timeout)
      timeoutId = setTimeout(() => {
        // console.log("⏱️ Company data loading timeout after 1.5 seconds - forcing stop");
        setIsLoadingCompanyData(false);
        // Set fallback if no name was loaded
        if (!nameLoaded) {
          // console.log("⚠️ Name not loaded, using empty string");
          setCompanyNAMEDetails("");
          nameLoaded = true;
        }
        if (!yearLoaded) {
          // console.log("⚠️ Year not loaded, using empty string");
          setCompanyYEAR("");
          yearLoaded = true;
        }
      }, 1500);

      const updateUrlDb =
        "/BusinessDetails/" +
        effectiveUid +
        `/ClientCompanyAdded/${companySelectedIdKey}/companyNameRef`;

      const newUpdateUrlDb = database.ref(updateUrlDb);
      const updateUrlDbYEAR =
        "/BusinessDetails/" +
        effectiveUid +
        `/ClientCompanyAdded/${companySelectedIdKey}/yearEndDate`;

      const newUpdateUrlDbYEAR = database.ref(updateUrlDbYEAR);

      newUpdateUrlDb.on("value", (snapshot) => {
        const accounts = snapshot.val();
        //  console.log("📊 Company name loaded:", accounts);
        setCompanyNAMEDetails(accounts || "");
        nameLoaded = true;
        checkLoadingComplete();
      });

      newUpdateUrlDbYEAR.on("value", (snapshot) => {
        const accounts = snapshot.val();
        //  console.log("📅 Company year loaded:", accounts);
        setCompanyYEAR(accounts || "");
        yearLoaded = true;
        checkLoadingComplete();
      });

      return () => {
        newUpdateUrlDb.off();
        newUpdateUrlDbYEAR.off();
        if (timeoutId) clearTimeout(timeoutId);
      };
    } else {
      // If we don't have companySelectedIdKey yet, wait
      if (!companySelectedIdKey) {
        //    console.log("⏳ Waiting for companySelectedIdKey...");
        setIsLoadingCompanyData(true);
      } else {
        // Have company but no uid - shouldn't happen, stop loading
        // console.log("⚠️ Have company but no uid - stopping load");
        setIsLoadingCompanyData(false);
        setCompanyNAMEDetails("");
        setCompanyYEAR("");
        lastLoadedCompanyRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companySelectedIdKey, uid, MainUserUID]);

  const handleLogout = async (e) => {
    e.preventDefault();
    try {
      setErrorMessage("");

      // firebase function to logout user -from context file
      await logout();

      //

      localStorage.removeItem("noSelection");
      // we can use useHistory from react router to push to the component in brackets.
      historyPage.push("/FeedBack");
    } catch {
      setErrorMessage("Logout unsuccessful");
    }
  };

  // Helper function to format company display name
  const getCompanyDisplayName = () => {
    // Use state for company info
    const effectiveCompanyName = CompanyNAMEDetails;
    const effectiveCompanyYear = CompanyYEAR;

    // Show loading state only when actively loading
    if (isLoadingCompanyData && MainUserUID) {
      return "Loading...";
    }

    // If we have a company selected but no name loaded, show a more appropriate message
    const displayName =
      effectiveCompanyName ||
      (companySelectedIdKey ? "Settings" : "");
    const displayYear = effectiveCompanyYear || "";

    // Show at least the company name, truncated if needed
    const truncatedName = displayName.slice(0, 30);
    const truncatedYear = displayYear.toString().slice(0, 4);

    return truncatedYear ? `${truncatedName} ${truncatedYear}` : truncatedName;
  };

  return (
    <div className="logout-wrapper">
      <style>
        {`
          @keyframes dotPulse {
            0%, 80%, 100% {
              transform: scale(0.4);
              opacity: 0.5;
            }
            40% {
              transform: scale(1);
              opacity: 1;
            }
          }
        `}
      </style>
      <nav
        className="logout-nav"
        aria-label="User session and company selection bar"
      >
        <div className="company-area">
          <Link
            to="/settings"
            style={{
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              gap: "6px",
              minWidth: "140px",
              maxWidth: "300px",
              width: "100%",
              padding: "8px 10px",
              fontWeight: "500",
              color: isDarkMode ? "#f3f4f6" : "#212529",
              background: isDarkMode
                ? "rgba(255,255,255,0.25)"
                : "rgba(0,0,0,0.05)",
              border: "1px solid rgba(255, 255, 255, 0.25)",
              borderRadius: "3px",
              textDecoration: "none",
              transition:
                "background 120ms cubic-bezier(.4,0,.2,1), transform 120ms cubic-bezier(.4,0,.2,1), box-shadow 120ms cubic-bezier(.4,0,.2,1)",
              position: "relative",
              overflow: "hidden",
              lineHeight: "1.1",
              whiteSpace: "nowrap",
            }}
            onMouseOver={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(255,255,255,0.25)"
                : "rgba(0,0,0,0.1)";
              e.target.style.transform = "translateY(-1px)";
            }}
            onMouseOut={(e) => {
              e.target.style.background = isDarkMode
                ? "rgba(255,255,255,0.25)"
                : "rgba(0,0,0,0.05)";
              e.target.style.transform = "translateY(0)";
            }}
            title={
              (CompanyNAMEDetails || "Company Settings") +
              (CompanyYEAR ? ` (${CompanyYEAR.toString().slice(0, 4)})` : "")
            }
          >
            <span className="company-icon" aria-hidden="true">
              📊
            </span>
            <span
              className="company-text"
              data-testid="company-display"
              style={{
                display: "inline-block",
                textOverflow: "ellipsis",
                overflow: "hidden",
                maxWidth: "250px",
                fontFeatureSettings: '"liga" 1, "calt" 1',
                color: isDarkMode ? "#f3f4f6" : "#212529",
              }}
            >
              {getCompanyDisplayName() || "Settings"}
            </span>
            {isLoadingCompanyData && (
              <span
                className="dot-pulse"
                aria-hidden="true"
                style={{
                  position: "relative",
                  width: "6px",
                  height: "6px",
                  borderRadius: "50%",
                  background: isDarkMode ? "#f3f4f6" : "#212529",
                  opacity: 0.75,
                  marginLeft: "4px",
                  animation: "dotPulse 1.2s infinite ease-in-out",
                }}
              />
            )}
          </Link>
        </div>
        <button
          className="logout-btn"
          onClick={handleLogout}
          aria-label="Logout of application"
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: "6px",
            padding: "8px 14px",
            fontWeight: "500",
            fontSize: "14px",
            background: "#dc3545",
            color: "#fff",
            border: "1px solid #b02a37",
            borderRadius: "3px",
            cursor: "pointer",
            lineHeight: "1.1",
            transition:
              "background 120ms cubic-bezier(.4,0,.2,1), transform 120ms cubic-bezier(.4,0,.2,1), box-shadow 120ms cubic-bezier(.4,0,.2,1)",
            position: "relative",
            isolation: "isolate",
            boxShadow: "0 1px 2px rgba(0, 0, 0, 0.25)",
            width: "100%",
          }}
          onMouseOver={(e) => {
            e.target.style.background = "#c82333";
            e.target.style.transform = "translateY(-1px)";
            e.target.style.boxShadow = "0 3px 6px rgba(0, 0, 0, 0.25)";
          }}
          onMouseOut={(e) => {
            e.target.style.background = "#dc3545";
            e.target.style.transform = "translateY(0)";
            e.target.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.25)";
          }}
        >
          <span className="logout-icon" aria-hidden="true">
            🚪
          </span>
          <span className="logout-text">Logout</span>
        </button>
      </nav>
      {errorMessage && (
        <div className="alert alert-warning mt-2" role="alert">
          {errorMessage}
        </div>
      )}
    </div>
  );
};
export default Logout;
